import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import GetAppRounded from '@mui/icons-material/GetAppRounded';
import TudoPetsLogo from '../images/tp_logo_laranja.png';
import { usePwaInstall } from '../hooks/usePwaInstall';
import {
  layoutIconButtonRadius,
  layoutShopSheetRadius,
  lead900,
  navBarInactiveCircle,
  orange600,
} from '../theme/colors';

const SLIDES = [
  {
    id: 'launch',
    title: 'Lançamento Tudopets',
    subtitle: 'O marketplace pet de Curitiba chegou.',
    bgcolor: lead900,
  },
  {
    id: 'install-app',
    title: 'Baixe o App!',
    subtitle: 'Instale na tela inicial para acesso rápido.',
    bgcolor: orange600,
  },
];

const slideShellSx = {
  flex: '0 0 100%',
  minWidth: '100%',
  height: '100%',
  borderRadius: layoutShopSheetRadius,
  scrollSnapAlign: 'start',
  scrollSnapStop: 'always',
  userSelect: 'none',
  overflow: 'hidden',
};

const navButtonSx = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  borderRadius: layoutIconButtonRadius,
  bgcolor: 'rgba(255, 255, 255, 0.92)',
  color: 'text.primary',
  boxShadow: 1,
  '&:hover': {
    bgcolor: 'common.white',
  },
};

const LaunchSlide = ({ slide, ariaHidden }) => (
  <Box
    aria-hidden={ariaHidden}
    sx={{
      ...slideShellSx,
      bgcolor: slide.bgcolor,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      px: { xs: 5, sm: 6 },
      py: 2,
      textAlign: 'center',
    }}
  >
    <Box
      component="img"
      src={TudoPetsLogo}
      alt=""
      aria-hidden
      sx={{
        width: { xs: 72, sm: 88 },
        height: 'auto',
        objectFit: 'contain',
      }}
    />
    <Typography variant="sectionTitle" sx={{ color: 'common.white' }}>
      {slide.title}
    </Typography>
    <Typography
      sx={{
        color: 'common.white',
        fontFamily: 'Nunito',
        fontSize: '14px',
        lineHeight: '21px',
        opacity: 0.9,
      }}
    >
      {slide.subtitle}
    </Typography>
  </Box>
);

const getInstallMessage = ({ canPromptInstall, isAppInstalled, isIos, subtitle }) => {
  if (canPromptInstall) return subtitle;
  if (isAppInstalled) {
    return 'App já instalado. Abra pelo menu Iniciar ou pela lista de aplicativos.';
  }
  if (isIos) return 'Toque em Compartilhar e depois em Adicionar à Tela de Início.';
  return 'Use o menu do navegador para instalar este app.';
};

const InstallSlide = ({ slide, ariaHidden, canPromptInstall, isAppInstalled, isIos, onInstall }) => (
  <Box
    aria-hidden={ariaHidden}
    sx={{
      ...slideShellSx,
      bgcolor: slide.bgcolor,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
      px: { xs: 5, sm: 6 },
      py: 2,
      textAlign: 'center',
    }}
  >
    <Typography variant="sectionTitle" sx={{ color: 'common.white' }}>
      {slide.title}
    </Typography>
    <Typography
      sx={{
        color: 'common.white',
        fontFamily: 'Nunito',
        fontSize: '14px',
        lineHeight: '21px',
        opacity: 0.95,
      }}
    >
      {getInstallMessage({ canPromptInstall, isAppInstalled, isIos, subtitle: slide.subtitle })}
    </Typography>
    {canPromptInstall && (
      <Button
        type="button"
        variant="contained"
        color="inherit"
        startIcon={<GetAppRounded />}
        onClick={onInstall}
        sx={{
          bgcolor: 'common.white',
          color: 'text.primary',
          '&:hover': {
            bgcolor: 'grey.100',
          },
        }}
      >
        Instalar app
      </Button>
    )}
  </Box>
);

const WelcomeCarousel = () => {
  const scrollRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const scrollUnlockTimer = useRef(null);
  const activeSlideRef = useRef(0);
  const { canPromptInstall, isStandalone, isAppInstalled, isIos, promptInstall } = usePwaInstall();
  const [isReady, setIsReady] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const visibleSlides = useMemo(
    () => SLIDES.filter((slide) => slide.id !== 'install-app' || !isStandalone),
    [isStandalone]
  );

  const slideCount = visibleSlides.length;
  const showControls = slideCount > 1;

  const getClosestIndex = useCallback(() => {
    const carousel = scrollRef.current;

    if (!carousel?.children.length) return 0;

    const index = Math.round(carousel.scrollLeft / carousel.clientWidth);

    return Math.min(Math.max(index, 0), carousel.children.length - 1);
  }, []);

  const centerSlide = useCallback((index, behavior = 'smooth') => {
    const carousel = scrollRef.current;
    const slide = carousel?.children[index];

    if (!carousel || !slide) return;

    window.clearTimeout(scrollUnlockTimer.current);
    isProgrammaticScroll.current = true;
    carousel.style.scrollSnapType = 'none';

    const targetLeft = slide.offsetLeft;

    if (behavior === 'auto') {
      carousel.scrollLeft = targetLeft;
      carousel.style.scrollSnapType = '';
      isProgrammaticScroll.current = false;
      return;
    }

    carousel.scrollTo({ left: targetLeft, behavior: 'smooth' });

    const unlockScroll = () => {
      carousel.style.scrollSnapType = '';
      isProgrammaticScroll.current = false;
    };

    if ('onscrollend' in carousel) {
      const handleScrollEnd = () => {
        unlockScroll();
        carousel.removeEventListener('scrollend', handleScrollEnd);
      };
      carousel.addEventListener('scrollend', handleScrollEnd, { once: true });
    } else {
      scrollUnlockTimer.current = window.setTimeout(unlockScroll, 400);
    }
  }, []);

  const goPrev = () => {
    const prev = ((activeSlideRef.current - 1) % slideCount + slideCount) % slideCount;
    activeSlideRef.current = prev;
    setActiveSlide(prev);
    centerSlide(prev, 'smooth');
  };

  const goNext = () => {
    const next = (activeSlideRef.current + 1) % slideCount;
    activeSlideRef.current = next;
    setActiveSlide(next);
    centerSlide(next, 'smooth');
  };

  useEffect(() => {
    activeSlideRef.current = activeSlide;
  }, [activeSlide]);

  const handleInstall = () => {
    promptInstall();
  };

  useLayoutEffect(() => {
    if (slideCount === 0) return;

    centerSlide(0, 'auto');
    activeSlideRef.current = 0;
    setActiveSlide(0);
    setIsReady(true);

    return () => {
      window.clearTimeout(scrollUnlockTimer.current);
    };
  }, [slideCount, centerSlide]);

  const handleScroll = () => {
    if (!isReady || isProgrammaticScroll.current) return;
    const closestIndex = getClosestIndex();
    activeSlideRef.current = closestIndex;
    setActiveSlide(closestIndex);
  };

  if (slideCount === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        visibility: isReady ? 'visible' : 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '176px', sm: '220px' },
        }}
      >
        <Box
          ref={scrollRef}
          role="region"
          aria-roledescription="carousel"
          aria-label="Anúncios"
          onScroll={handleScroll}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            overflowX: showControls ? 'auto' : 'hidden',
            overflowY: 'hidden',
            scrollSnapType: showControls ? 'x mandatory' : 'none',
            scrollBehavior: isReady ? 'smooth' : 'auto',
            touchAction: showControls ? 'pan-x' : 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}
        >
          {visibleSlides.map((slide) => {
            const slideProps = {
              slide,
              ariaHidden: visibleSlides[activeSlide]?.id !== slide.id,
            };

            if (slide.id === 'launch') {
              return <LaunchSlide key={slide.id} {...slideProps} />;
            }

            return (
              <InstallSlide
                key={slide.id}
                {...slideProps}
                canPromptInstall={canPromptInstall}
                isAppInstalled={isAppInstalled}
                isIos={isIos}
                onInstall={handleInstall}
              />
            );
          })}
        </Box>

        {showControls && (
          <>
            <IconButton
              type="button"
              onClick={goPrev}
              aria-label="Slide anterior"
              sx={{
                ...navButtonSx,
                left: { xs: -6, sm: -8 },
              }}
            >
              <ChevronLeftRounded />
            </IconButton>
            <IconButton
              type="button"
              onClick={goNext}
              aria-label="Próximo slide"
              sx={{
                ...navButtonSx,
                right: { xs: -6, sm: -8 },
              }}
            >
              <ChevronRightRounded />
            </IconButton>
          </>
        )}
      </Box>

      {showControls && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} aria-hidden>
          {visibleSlides.map((slide, index) => (
            <Box
              key={slide.id}
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: activeSlide === index ? 'primary.main' : navBarInactiveCircle,
                transition: 'background-color 160ms ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WelcomeCarousel;
